/**
 * Chunk U3 — Review uploads UI helpers (sidebar chips, focus, per-file actions).
 */

import {
  UPLOAD_DRAFT_STATUS,
  UPLOAD_QUEUE_COPY,
  pickReadyFirstFocusId,
  sortUploadDraftsByPickOrder,
  transitionUploadDraft,
} from './invoiceUploadQueueHelpers'
import { isUploadFileComplete } from './invoiceUploadHelpers'

export const UPLOAD_REVIEW_STATUS_CHIP = {
  [UPLOAD_DRAFT_STATUS.QUEUED]: { label: 'Waiting', tone: 'muted' },
  [UPLOAD_DRAFT_STATUS.READING]: { label: 'Reading', tone: 'info' },
  [UPLOAD_DRAFT_STATUS.READY]: { label: 'Ready', tone: 'success' },
  [UPLOAD_DRAFT_STATUS.CONFIRMING]: { label: 'Saving…', tone: 'info' },
  [UPLOAD_DRAFT_STATUS.CONFIRMED]: { label: 'Confirmed', tone: 'success' },
  [UPLOAD_DRAFT_STATUS.SKIPPED]: { label: 'Skipped', tone: 'muted' },
  [UPLOAD_DRAFT_STATUS.FAILED]: { label: 'Needs retry', tone: 'danger' },
  [UPLOAD_DRAFT_STATUS.DISCARDED]: { label: 'Removed', tone: 'muted' },
}

export function getUploadReviewStatusChip(status) {
  const key = String(status || '').trim()
  return (
    UPLOAD_REVIEW_STATUS_CHIP[key] || {
      label: key || 'Unknown',
      tone: 'muted',
    }
  )
}

/** Sidebar rows in original pick order (includes confirmed/skipped for context). */
export function buildUploadReviewSidebarItems(drafts = [], files = []) {
  const filesByDraftId = new Map(
    (files || [])
      .filter(f => f?.draftId != null)
      .map(f => [String(f.draftId), f]),
  )
  return sortUploadDraftsByPickOrder(drafts || [])
    .filter(d => String(d?.status) !== UPLOAD_DRAFT_STATUS.DISCARDED)
    .map(draft => {
      const file = filesByDraftId.get(String(draft.id))
      const chip = getUploadReviewStatusChip(draft.status)
      return {
        id: String(draft.id),
        pickIndex: draft.pickIndex,
        filename:
          file?.filename ||
          draft.filename ||
          `File ${(draft.pickIndex || 0) + 1}`,
        documentType: file?.documentType || draft.documentType || 'invoice',
        status: draft.status,
        chipLabel: chip.label,
        chipTone: chip.tone,
        selectable: true,
      }
    })
}

export function findUploadReviewFile(files = [], draftId) {
  if (draftId == null) return null
  return (files || []).find(f => String(f?.draftId) === String(draftId)) || null
}

export function findUploadReviewDraft(drafts = [], draftId) {
  if (draftId == null) return null
  return (drafts || []).find(d => String(d?.id) === String(draftId)) || null
}

/** Focus card is editable when OCR finished (ready/failed) or confirming. */
export function isUploadReviewFocusEditable(status) {
  const s = String(status || '')
  return (
    s === UPLOAD_DRAFT_STATUS.READY ||
    s === UPLOAD_DRAFT_STATUS.FAILED ||
    s === UPLOAD_DRAFT_STATUS.CONFIRMING
  )
}

export function canConfirmUploadReviewFocus(file, draft) {
  if (!file || !draft) return false
  if (!isUploadReviewFocusEditable(draft.status)) return false
  if (draft.status === UPLOAD_DRAFT_STATUS.CONFIRMING) return false
  return isUploadFileComplete(file)
}

/** Open session rows eligible for batch confirm (excludes skipped / discarded). */
export function listUploadReviewConfirmableItems(drafts = [], files = []) {
  return getOpenUploadReviewDrafts(drafts)
    .filter(d => String(d?.status) !== UPLOAD_DRAFT_STATUS.SKIPPED)
    .map(draft => ({
      draft,
      file: findUploadReviewFile(files, draft.id),
    }))
    .filter(pair => pair.file && pair.draft)
}

export function canConfirmAllUploadReview(drafts = [], files = []) {
  const openDrafts = getOpenUploadReviewDrafts(drafts).filter(
    draft => String(draft.status) !== UPLOAD_DRAFT_STATUS.SKIPPED,
  )
  if (!openDrafts.length) return false
  if (
    openDrafts.some(draft => String(draft.status) !== UPLOAD_DRAFT_STATUS.READY)
  ) {
    return false
  }
  return openDrafts.every(draft => {
    const file = findUploadReviewFile(files, draft.id)
    return file && canConfirmUploadReviewFocus(file, draft)
  })
}

export function getConfirmAllUploadReviewLabel(drafts = [], files = []) {
  const count = listUploadReviewConfirmableItems(drafts, files).length
  return UPLOAD_QUEUE_COPY.confirmAll(count)
}

export function canSkipUploadReviewFocus(draft) {
  if (!draft) return false
  return String(draft.status) === UPLOAD_DRAFT_STATUS.READY
}

export function canRemoveUploadReviewFocus(draft) {
  if (!draft) return false
  const s = String(draft.status)
  return (
    s !== UPLOAD_DRAFT_STATUS.CONFIRMED &&
    s !== UPLOAD_DRAFT_STATUS.DISCARDED &&
    s !== UPLOAD_DRAFT_STATUS.CONFIRMING
  )
}

/**
 * Drafts still open in the review session (not confirmed / discarded).
 * Skipped + failed remain listed until discard / resume.
 */
export function getOpenUploadReviewDrafts(drafts = []) {
  return (drafts || []).filter(d => {
    const s = String(d?.status || '')
    return (
      s !== UPLOAD_DRAFT_STATUS.CONFIRMED && s !== UPLOAD_DRAFT_STATUS.DISCARDED
    )
  })
}

export function isUploadReviewSessionComplete(drafts = []) {
  const list = (drafts || []).filter(
    d => String(d?.status) !== UPLOAD_DRAFT_STATUS.DISCARDED,
  )
  if (!list.length) return true
  return list.every(d => {
    const s = String(d.status)
    return (
      s === UPLOAD_DRAFT_STATUS.CONFIRMED || s === UPLOAD_DRAFT_STATUS.SKIPPED
    )
  })
}

/** After confirm/skip/remove — next ready-first focus (or null if done). */
export function pickFocusAfterUploadReviewAction(
  drafts = [],
  previousFocusId = null,
) {
  const open = getOpenUploadReviewDrafts(drafts).filter(
    d => String(d.status) !== UPLOAD_DRAFT_STATUS.SKIPPED,
  )
  // Prefer ready among remaining active work; skipped stay in sidebar but not focus.
  return pickReadyFirstFocusId(open, previousFocusId)
}

/**
 * Outcome after Remove in the review modal.
 * Keeps the modal open (no screen refresh) — advances focus or shows done state.
 */
export function getUploadReviewRemoveOutcome(
  drafts = [],
  removedDraftId = null,
) {
  const remaining = getOpenUploadReviewDrafts(drafts)
  return {
    keepModalOpen: true,
    refreshLibrary: false,
    showFullModalLoading: false,
    sessionEmpty: remaining.length === 0,
    nextFocusId: remaining.length
      ? pickFocusAfterUploadReviewAction(drafts, removedDraftId)
      : null,
  }
}

export function markUploadDraftConfirming(draft) {
  return transitionUploadDraft(draft, UPLOAD_DRAFT_STATUS.CONFIRMING)
}

export function markUploadDraftConfirmed(draft) {
  return transitionUploadDraft(draft, UPLOAD_DRAFT_STATUS.CONFIRMED)
}

export function markUploadDraftSkipped(draft) {
  return transitionUploadDraft(draft, UPLOAD_DRAFT_STATUS.SKIPPED)
}

export function markUploadDraftDiscarded(draft) {
  if (!draft) return draft
  if (String(draft.status) === UPLOAD_DRAFT_STATUS.DISCARDED) return draft
  if (String(draft.status) === UPLOAD_DRAFT_STATUS.CONFIRMED) return draft
  let current = draft
  if (String(current.status) === UPLOAD_DRAFT_STATUS.CONFIRMING) {
    current = transitionUploadDraft(current, UPLOAD_DRAFT_STATUS.READY)
  }
  return transitionUploadDraft(current, UPLOAD_DRAFT_STATUS.DISCARDED)
}

export function getUploadReviewFooterCopy() {
  return {
    reviewLater: UPLOAD_QUEUE_COPY.reviewLater,
    discardRemaining: UPLOAD_QUEUE_COPY.discardRemaining,
    discardRemainingConfirm: UPLOAD_QUEUE_COPY.discardRemainingConfirm,
    discardRemainingOk: UPLOAD_QUEUE_COPY.discardRemainingOk,
    discardRemainingCancel: UPLOAD_QUEUE_COPY.discardRemainingCancel,
    confirmThis: UPLOAD_QUEUE_COPY.confirmThis,
    confirmAll: UPLOAD_QUEUE_COPY.confirmAll,
    confirmAllHint: UPLOAD_QUEUE_COPY.confirmAllHint,
    removeThis: UPLOAD_QUEUE_COPY.removeThis,
    allDoneTitle: UPLOAD_QUEUE_COPY.allDoneTitle,
    allDoneBody: UPLOAD_QUEUE_COPY.allDoneBody,
    cnInQueueHint: UPLOAD_QUEUE_COPY.cnInQueueHint,
  }
}

export function resolveUploadReviewFocusId(
  drafts = [],
  currentFocusId = null,
  { userPicked = false } = {},
) {
  if (userPicked && currentFocusId) {
    const stillThere = (drafts || []).some(
      d =>
        String(d.id) === String(currentFocusId) &&
        String(d.status) !== UPLOAD_DRAFT_STATUS.DISCARDED,
    )
    if (stillThere) return String(currentFocusId)
  }
  return pickReadyFirstFocusId(drafts, currentFocusId)
}
